import {
  filter as lodashFilter,
  isString,
  each,
  isObject,
  map,
  reduce,
  capitalize,
  isEmpty,
  min,
  sortBy,
  pull,
  initial,
} from 'lodash';

function Helper($q, $cacheFactory, $timeout, $window, gettextCatalog, gettext, configuration) {
  'ngInject';
  var _cache = $cacheFactory('adjust');

  return {
    useCache: useCache,
    removeCache: removeCache,
    filter: filter,
    translateEvent: translateEvent,
    translateKpi: translateKpi,
    getTranslation: getTranslation,
    getRegExp: getRegExp,
    getName: getName,
    renderTrace: renderTrace,
    getPeriod: getPeriod,
    getErrorMessage: getErrorMessage,
    destroyTid: destroyTid,
    ready: ready,
    prepareStateName: prepareStateName,
    getMinDate: getMinDate,
    linkFunction: linkFunction,
    deleteCache: deleteCache,
    secondsToDuration: secondsToDuration,
    sortByProperty: sortByProperty,
    validateEmail: validateEmail,
    humanizeArray: humanizeArray,
    notEqual: notEqual,
    shortNumber: shortNumber,
    pluralize: pluralize,
    getAbsoluteUrl: getAbsoluteUrl,
  };

  /**
   * @ngdoc method
   * @name deleteCache
   * @methodOf adjust.service:Helper
   *
   * @description
   * Deletes the cache data of a given key
   *
   */
  function deleteCache(cacheKey) {
    _cache.remove(cacheKey);
  }

  /**
   * @ngdoc method
   * @name useCache
   * @methodOf adjust.service:Helper
   *
   * @description
   * Use cache if available for particular resource
   *
   * @returns {promise} A promise representing the state of the resource request
   */
  function useCache(resource, cacheKey) {
    // TODO invalidate cache after some time
    var cached = _cache.get(cacheKey);
    var deferred = $q.defer();

    if (cached) {
      deferred.resolve(cached);
    } else {
      resource().$promise.then(
        function(result) {
          _cache.put(cacheKey, result);

          deferred.resolve(result);
        },
        function(error) {
          deferred.reject(error);
        },
      );
    }

    return deferred.promise;
  }

  /**
   * @ngdoc removeCache
   * @name useCache
   * @methodOf adjust.service:Helper
   *
   * @description
   * Remove cache
   *
   */
  function removeCache(cacheKey) {
    if (cacheKey) {
      _cache.remove(cacheKey);
    } else {
      _cache.removeAll();
    }
  }

  /**
   * @ngdoc method
   * @name filter
   * @methodOf adjust.service:Helper
   *
   * @description
   * Filter array value but as case insensitive
   *
   * @param {Array} array array to filter
   * @param {string} term search term
   * @param {string=} key key to filter
   * @param {Function} parser Custom parser for deep objects
   * @returns {Array} filtered array
   */
  function filter(array, term, key, parser) {
    key = key || 'name';
    parser =
      parser ||
      function(item) {
        return item[key];
      };
    term = term.includes('https://app.adjust.com') ? term.split('/')[3] : term || '';
    term = _normalizeWhitespaces(term);
    // eslint-disable-next-line no-useless-escape
    var escaped = term.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');

    return lodashFilter(array, function(item) {
      var regexp = new RegExp(escaped, 'i');
      return regexp.test(parser(item)) || item.token === term;
    });
  }

  /**
   * @ngdoc method
   * @name _normalizeWhitespaces
   * @methodOf adjust.service:Helper
   *
   * @description
   * Removes extra whitespaces
   *
   * @param {string} term
   * @returns {string} normalized term
   * @private
   */
  function _normalizeWhitespaces(str) {
    return str ? str.replace(/(\s)+/, '$1') : str;
  }

  /**
   * @ngdoc method
   * @name translateEvent
   * @methodOf adjust.service:Helper
   *
   * @description
   * Translate kpi name or return capitalized event name
   *
   * @param {Object|string} event event details
   * @param {string} event.name name of the event
   * @param {string} event.token token of the event
   * @param {boolean=false} short use short version of the event name
   * @returns {string} translated kpi or event name
   */
  function translateEvent(event, short) {
    event = isString(event) ? { name: event, token: event } : event;

    var translation;

    if (event && event.name === event.token) {
      if (short) {
        translation = getTranslation(`kpi.${event.name}_short`);
        if (!translation) {
          translation = getTranslation(event.name);
        }
      } else {
        translation = getTranslation(event.name);
      }

      return translation;
    }

    return (event && event.name) || '';
  }

  /**
   * @ngdoc method
   * @name translateKpi
   * @methodOf adjust.service:Helper
   *
   * @description
   * Translate the kpi and interpolate period if available
   *
   * @param {string} kpi kpi name
   * @param {string=} period period to interpolate, day|week|month
   * @param {string} token kpi token
   * @returns {string} translated string
   */
  function translateKpi(kpi, period, token) {
    const periodLabel =
      {
        day: gettext('label.day'),
        week: gettext('label.week'),
        month: gettext('label.month'),
      }[period] || period;

    if (!kpi) {
      return '';
    } else if (token && kpi !== token) {
      /**
       * Custom event name should not be modified (translated).
       *
       * To distinguish between default kpi and custom event,
       * we can compare its name and token.
       * Default kpi has the same name as the token.
       */
      return kpi;
    }

    return t(kpi, { period: t(periodLabel) });
  }

  /**
   * @ngdoc method
   * @name getTranslation
   * @methodOf adjust.service:Helper
   *
   * @description
   * Get translation of the key if exists
   *
   * @param {string} translationKey key to translate
   * @param {Object=} params parameters to interpolate
   * @returns {string} translation
   * @private
   */
  function getTranslation(translationKey, params) {
    var translationValue = gettextCatalog.getString(translationKey, params);

    if (translationValue.includes('[MISSING]:')) {
      translationValue = translationValue.replace('[MISSING]:', '').trim();
    }

    if (translationKey === translationValue) {
      return '';
    }

    return translationValue;
  }

  /**
   * @ngdoc method
   * @name getRegExp
   * @methodOf adjust.service:Helper
   *
   * @description
   * Build regular expression from string which has
   * structure as regular expression but it needs to be parsed
   *
   * @param {string} pattern regexp pattern
   * @returns {RegExp} regular expression object
   */
  function getRegExp(pattern) {
    var lastIndex = pattern.lastIndexOf('/');
    var regexpString = pattern.slice(1, lastIndex);
    var regexpFlags = pattern.slice(lastIndex + 1);

    return new RegExp(regexpString, regexpFlags);
  }

  /**
   * @ngdoc method
   * @name renderTrace
   * @methodOf adjust.service:Helper
   *
   * @description
   * Render trace structure used by {@link adjust.directive:status status} directive
   *
   * @param {string} status success or error status
   * @param {string} message status message
   */
  function renderTrace(status, message) {
    return {
      status: status,
      message: message,
    };
  }

  /**
   * @ngdoc method
   * @name getName
   * @methodOf adjust.service:Helper
   *
   * @description
   * Generate the name out of arguments
   */
  function getName() {
    var args = [];

    each(Array.prototype.slice.call(arguments), function(arg) {
      args.push(
        String(arg)
          .toLowerCase()
          .replace(/\s/g, '-'),
      );
    });

    return args.join('-');
  }

  /**
   * @ngdoc method
   * @name getPeriod
   * @methodOf adjust.service:Helper
   *
   * @description
   * Get translation of the period in plural
   *
   * @param {string} period time period, day, week or month
   * @returns {string} period translation in plural
   */
  function getPeriod(period) {
    var translated;

    switch (period) {
      case 'day':
        translated = gettextCatalog.getString('label.days');
        break;
      case 'week':
        translated = gettextCatalog.getString('label.weeks');
        break;
      case 'month':
        translated = gettextCatalog.getString('label.months');
        break;
      default:
        translated = period;
    }

    return translated;
  }

  /**
   * @ngdoc method
   * @name getErrorMessage
   * @methodOf adjust.service:Helper
   *
   * @description
   * Get error messages or fallback to default error message
   *
   * @param {Object=} data error data object
   * @returns {string} error message
   */
  function getErrorMessage(data) {
    var error = data && data.errors ? data.errors : [];
    var defaultMessage = gettextCatalog.getString('error.an-error-occurred.try-again-later');

    if (isString(error)) {
      error = [error];
    } else if (isObject(error) && !Array.isArray(error)) {
      error = map(error, function(values) {
        return values.join(', ');
      });
    }

    return Object.values(error).join(', ') || defaultMessage;
  }

  /**
   * @ngdoc method
   * @name destroyTid
   * @methodOf adjust.service:Helper
   *
   * @description
   * Destroy particular timeout id
   *
   * @param {Object} tid timeout id promise
   */
  function destroyTid(tid) {
    if (!tid) {
      return;
    }

    $timeout.cancel(tid);
    tid = null;
  }

  /**
   * @ngdoc method
   * @name ready
   * @methodOf adjust.service:Helper
   *
   * @description
   * Running callback through timeout in order to ensure that digest cycle has been done
   *
   * @param {Function} cb callback to be run after digest cycle
   * @returns {promise} timeout promise
   */
  function ready(cb) {
    return $timeout(cb);
  }

  /**
   * @ngdoc method
   * @name prepareStateName
   * @methodOf adjust.service:Helper
   *
   * @description
   * Prepare the state name by camel-casing it
   *
   * @param {string} name screen name
   */
  function prepareStateName(name) {
    return reduce(name.split('-'), function(sum, part) {
      return sum + capitalize(part);
    }, '');
  }

  /**
   * @ngdoc method
   * @name getMinDate
   * @methodOf adjust.service:Helper
   *
   * @description
   * Determine the minimum date
   *
   * @param {Array|Object} dates array of dates
   * @param {string} fallbackDate fallback option if no dates
   */
  function getMinDate(dates, fallbackDate) {
    if (isEmpty(dates)) {
      return fallbackDate;
    }

    return min(Object.values(dates));
  }

  /**
   * @ngdoc method
   * @name linkFunction
   * @methodOf adjust.service:Helper
   *
   * @description
   * Common link function for dependant directives' controllers
   *
   */
  function linkFunction() {
    var [, , , ctrls] = arguments;
    var [childController, parentController] = ctrls;

    childController.init(parentController);
  }

  function secondsToDuration(seconds) {
    var result = {
      hours: 0,
      minutes: 0,
      seconds: 0,
    };

    if (!seconds) {
      return result;
    }

    result.hours = Math.floor(seconds / 3600);
    seconds -= result.hours * 3600;

    result.minutes = Math.floor(seconds / 60);
    seconds -= result.minutes * 60;

    result.seconds = parseInt(seconds % 60, 10);

    return result;
  }

  function sortByProperty(collection, property) {
    return sortBy(collection, function(item) {
      return String(item[property]).toLowerCase();
    });
  }

  /**
   * @ngdoc method
   * @name validateEmail
   * @methodOf adjust.service:Helper
   *
   * @description
   * Remove email from the list if invalid
   *
   * @param {string} item email to validate
   * @param {array} list list of input values
   */
  function validateEmail(item, list) {
    if (/^([^@\s]+)@((?:[-a-z0-9]+\.)+[a-z]{2,})$/i.test(item)) {
      return;
    }

    pull(list, item);
  }

  /**
   * @ngdoc method
   * @name humanizeArray
   * @methodOf adjust.service:Helper
   *
   * @description
   * Format array in a human readable way
   *
   * @param {Array} array array to format
   * @param {string} cnj array's end conjunction
   * @returns {string} human readable stringified array
   */
  function humanizeArray(array, cnj) {
    array = array || [];
    cnj = cnj || 'and';

    var conjunctions = {
      and: gettext('label.and'),
      or: gettext('label.or'),
    };

    if (array.length <= 1) {
      return array.join('');
    }

    return [initial(array).join(', '), gettextCatalog.getString(conjunctions[cnj]), array[array.length - 1]].join(' ');
  }

  /**
   * @ngdoc method
   * @name notEqual
   * @methodOf adjust.service:Helper
   *
   * @description
   * Check if two objects/strings/numbers are not equal
   *
   * @param {*} one item to compare
   * @param {*} two item to compare
   * @returns {boolean} is equal or not
   */
  function notEqual(one, two) {
    return JSON.stringify(one) !== JSON.stringify(two);
  }

  /**
   * @ngdoc method
   * @name shortNumber
   * @methodOf adjust.service:Helper
   *
   * @description
   * Shorten the number to its abbreviated form (K,M,B,T)
   *
   * @param {Integer} number number to be converted
   * @returns {String} Abbreviated form
   */
  function shortNumber(number, shortAbove) {
    var thousand = 1000;
    var million = 1000000;
    var billion = 1000000000;
    var trillion = 1000000000000;

    var threshold = {
      K: thousand,
      M: million,
      B: billion,
      T: trillion,
    };

    if (!shortAbove || number < threshold[shortAbove]) {
      return [number, ''];
    }

    if (number >= trillion) {
      return [number / trillion, 'T'];
    }

    if (number < trillion && number >= billion) {
      return [number / billion, 'B'];
    }

    if (number < billion && number >= million) {
      return [number / million, 'M'];
    }

    if (number < million && number >= thousand) {
      return [number / thousand, 'K'];
    }

    return [number, ''];
  }

  /**
   * Pick between singular and plural based on a count
   *
   * @param {Number} count
   * @param {String} singular
   * @param {String} plural
   * @returns {String}
   */
  function pluralize(count, singular, plural) {
    return count === 1 ? singular : plural;
  }

  /**
   * Creates absolute URL with Base domain
   *
   * @param {String} path
   * @returns {String}
   */
  function getAbsoluteUrl(path) {
    const baseUrl = __ENV__ === 'production' ? configuration.angularDashboard.baseUrl : $window.location.origin;

    return [baseUrl, path.replace(/^\/+/, '')].join('/');
  }
}

export { Helper };
